import React from 'react';
import './MenuSkeleton.css';

interface MenuSkeletonProps {
  count?: number;
}

const Shimmer: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span className={`ms-shimmer ${className}`.trim()} aria-hidden="true" />
);

export const MenuCategoryStripSkeleton: React.FC = () => (
  <div
    className="category-badge-strip category-badge-strip--compact category-badge-strip-skeleton"
    aria-hidden="true"
  >
    {Array.from({ length: 5 }).map((_, i) => (
      <Shimmer key={i} className="menu-skel-category-badge" />
    ))}
  </div>
);

export const MenuToolbarSkeleton: React.FC = () => (
  <div className="menu-toolbar" aria-hidden="true">
    <div className="filter-pills">
      <Shimmer className="menu-skel-pill menu-skel-pill--veg" />
      <Shimmer className="menu-skel-pill menu-skel-pill--nonveg" />
    </div>
    <div className="toolbar-right">
      <Shimmer className="menu-skel-toolbar-btn menu-skel-toolbar-btn--bookmark" />
      <Shimmer className="menu-skel-toolbar-btn menu-skel-toolbar-btn--filter" />
      <Shimmer className="menu-skel-toolbar-btn menu-skel-toolbar-btn--checkout" />
    </div>
  </div>
);

export const MenuResultsMetaSkeleton: React.FC = () => (
  <div className="menu-results-meta" aria-hidden="true">
    <Shimmer className="menu-skel-results-count" />
    <Shimmer className="menu-skel-sort" />
  </div>
);

export const MenuItemSkeleton: React.FC = () => (
  <div className="menu-item-card menu-card menu-item-skeleton" aria-hidden="true">
    <div className="card-img-wrap menu-item-image menu-item-skeleton__image">
      <Shimmer className="menu-item-skeleton__img-fill" />
      <Shimmer className="menu-item-skeleton__veg-dot" />
      <Shimmer className="menu-item-skeleton__bookmark" />
      <Shimmer className="menu-item-skeleton__tag" />
    </div>
    <div className="card-body menu-item-skeleton__body">
      <Shimmer className="menu-item-skeleton__title" />
      <Shimmer className="menu-item-skeleton__desc" />
      <Shimmer className="menu-item-skeleton__desc menu-item-skeleton__desc--short" />
      <div className="menu-item-skeleton__badges">
        <Shimmer className="menu-item-skeleton__badge" />
      </div>
      <div className="menu-item-skeleton__footer">
        <div className="menu-item-skeleton__prices">
          <Shimmer className="menu-item-skeleton__mrp" />
          <Shimmer className="menu-item-skeleton__price" />
        </div>
        <Shimmer className="menu-item-skeleton__qty" />
      </div>
    </div>
  </div>
);

export const MenuListSkeleton: React.FC<MenuSkeletonProps> = ({ count = 6 }) => (
  <div className="menu-items-grid menu-list-skeleton" aria-hidden="true">
    <div className="section-label menu-skel-section-label">
      <Shimmer className="menu-skel-section-text" />
    </div>
    {Array.from({ length: count }).map((_, index) => (
      <MenuItemSkeleton key={index} />
    ))}
  </div>
);

export const FilterSkeleton: React.FC = () => (
  <div className="filter-skeleton" aria-hidden="true">
    <div className="filter-section">
      <Shimmer className="filter-skeleton__title" />
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="filter-skeleton__row">
          <Shimmer className="filter-skeleton__check" />
          <Shimmer className="filter-skeleton__label" />
        </div>
      ))}
    </div>
    <div className="filter-section">
      <Shimmer className="filter-skeleton__title filter-skeleton__title--narrow" />
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="filter-skeleton__row">
          <Shimmer className="filter-skeleton__check" />
          <Shimmer className="filter-skeleton__label filter-skeleton__label--medium" />
        </div>
      ))}
    </div>
    <div className="filter-section">
      <Shimmer className="filter-skeleton__title filter-skeleton__title--narrow" />
      <div className="filter-skeleton__tags tag-list">
        {Array.from({ length: 4 }).map((_, index) => (
          <Shimmer key={index} className="filter-skeleton__tag" />
        ))}
      </div>
    </div>
    <div className="filter-section filter-section--price">
      <Shimmer className="filter-skeleton__title filter-skeleton__title--narrow" />
      <div className="price-range-slider">
        <Shimmer className="filter-skeleton__slider" />
        <Shimmer className="filter-skeleton__price-label" />
      </div>
    </div>
    <Shimmer className="filter-skeleton__clear-btn" />
  </div>
);

/** Mirrors loaded menu layout — breadcrumb stays in RestaurantMenu shell above this. */
export const MenuPageSkeleton: React.FC = () => (
  <div className="menu-layout-fullbleed" aria-busy="true" aria-label="Loading menu">
    <div className="menu-layout">
      <aside className="sidebar-left menu-filter-sidebar">
        <FilterSkeleton />
      </aside>
      <div className="menu-main-content">
        <MenuCategoryStripSkeleton />
        <MenuToolbarSkeleton />
        <MenuResultsMetaSkeleton />
        <MenuListSkeleton count={6} />
      </div>
    </div>
  </div>
);

export default MenuListSkeleton;
